import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import UserMenu from './UserMenu';

const Navbar: React.FC = () => {
  const { translations, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navItems = [
    { path: '/', label: translations.nav.home },
    { path: '/transport', label: translations.nav.transport },
    { path: '/stay', label: translations.nav.stay },
    { path: '/tickets', label: translations.nav.tickets },
    { path: '/cowork', label: translations.nav.cowork },
    { path: '/culture', label: translations.nav.culture },
    { path: '/nomad', label: translations.nav.nomad },
  ];

  // 監聽滾動事件
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'zh-TW' ? 'en-US' : 'zh-TW');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'glass shadow-strong backdrop-blur-md' 
        : 'bg-white/80 backdrop-blur-sm shadow-soft'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Enhanced Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center group">
              <div className="w-10 h-10 bg-gradient-to-br from-hualien-blue to-hualien-green rounded-2xl flex items-center justify-center shadow-medium group-hover:shadow-strong transition-all duration-300 group-hover:scale-110">
                <span className="text-white font-bold text-lg">花</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gradient group-hover:scale-105 transition-transform duration-300">
                You Move
              </span>
            </Link>
          </div>

          {/* Desktop Navigation with Enhanced Design */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 group ${
                    location.pathname === item.path
                      ? 'bg-gradient-to-r from-hualien-blue to-blue-600 text-white shadow-medium'
                      : 'text-gray-700 hover:text-hualien-blue hover:bg-hualien-light-blue/50'
                  }`}
                >
                  {item.label}
                  {location.pathname === item.path && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white rounded-full"></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-hualien-blue/10 to-blue-600/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
              ))}
            </div>
          </div>

          {/* Enhanced Language Toggle & Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            {/* 開始預約按鈕 */}
            <Link
              to="/start"
              className="hidden md:inline-flex items-center px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium shadow-soft"
            >              
              {language === 'zh-TW' ? '開始預約' : 'Start Booking'}
            </Link>

            {/* 用戶選單 */}
            <UserMenu />

            <button
              onClick={toggleLanguage}
              className="btn-animate px-4 py-2 rounded-xl text-sm font-medium text-gray-700 hover:text-hualien-blue hover:bg-hualien-light-blue/50 transition-all duration-300 border border-gray-200 hover:border-hualien-blue/30 shadow-soft hover:shadow-medium"
            >
              <span className="flex items-center">
                <span className="mr-1">🌐</span>
                {language === 'zh-TW' ? 'EN' : '中'}
              </span>
            </button>

            {/* Enhanced Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="btn-animate inline-flex items-center justify-center p-2 rounded-xl text-gray-700 hover:text-hualien-blue hover:bg-hualien-light-blue/50 focus:outline-none transition-all duration-300 border border-gray-200 hover:border-hualien-blue/30"
              >
                <svg
                  className="h-6 w-6 transition-transform duration-300"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden glass backdrop-blur-md border-t border-white/20">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {/* 移動版開始預約按鈕 */}
            <Link
              to="/start"
              className="block px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl text-center transition-all duration-300 shadow-medium hover:shadow-strong"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              🗺️ {language === 'zh-TW' ? '開始預約' : 'Start Booking'}
            </Link>
            
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                  location.pathname === item.path
                    ? 'bg-gradient-to-r from-hualien-blue to-blue-600 text-white shadow-medium'
                    : 'text-gray-700 hover:text-hualien-blue hover:bg-white/20'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 